import { __assign } from "tslib";
import { borderRadius as getBorderRadius, fontSize as getFontSize, gridSize as getGridSize, } from '@atlaskit/theme/constants';
import colors from './colors';
var borderRadius = getBorderRadius();
var gridSize = getGridSize();
var fontSize = getFontSize();
// ## Button layout
//
// /------------------------------------------------------------------------------------------------------------------\
// |       →  |  ←      |          |      →  |  ←      |           |      →  |  ←      |         |      →  |  ←       |
// |  10px →  |  ← 2px  |   icon   |  2px →  |  ← 2px  |           |  2px →  |  ← 2px  |  icon   |  2px →  |  ← 10px  |
// |  padding |  margin |  before  |  margin |  margin |  content  |  margin |  margin |  after  |  margin |  padding |
// |    (12px total)    |          |    (4px total)    |           |    (4px total)    |         |    (12px total)    |
// |       →  |  ←      |          |      →  |  ←      |           |      →  |  ←      |         |      →  |  ←       |
// \------------------------------------------------------------------------------------------------------------------/
//                                           ↑                               ↑
//                                        Margins don't collapse with inline-flex
//
var heights = {
    default: (gridSize * 4) / fontSize + "em",
    compact: (gridSize * 3) / fontSize + "em",
    none: 'auto',
};
var lineHeights = {
    default: heights.default,
    compact: heights.compact,
    none: 'inherit',
};
var padding = {
    // 10px gutter
    default: "0 " + (gridSize + gridSize / 4) + "px",
    compact: "0 " + (gridSize + gridSize / 4) + "px",
    none: '0',
};
var singleIconPadding = {
    // 2px gutter
    compact: "0 " + gridSize / 4 + "px",
    default: "0 " + gridSize / 4 + "px",
    none: '0',
};
var verticalAlign = {
    default: 'middle',
    compact: 'middle',
    none: 'baseline',
};
var innerMargin = {
    content: "0 " + gridSize / 4 + "px",
    icon: "0 " + gridSize / 4 + "px",
};
function getColor(_a) {
    var group = _a.group, key = _a.key, mode = _a.mode;
    var rule = group[key] || group.default;
    return rule[mode];
}
function getColors(_a) {
    var appearance = _a.appearance, key = _a.key, mode = _a.mode;
    return {
        background: getColor({
            group: colors.background[appearance],
            key: key,
            mode: mode,
        }),
        // Needing to add !important to overcome specificity issue caused by deprecated AtlaskitThemeProvider
        color: getColor({
            group: colors.color[appearance],
            key: key,
            mode: mode,
        }) + " !important",
    };
}
export function getCss(_a) {
    var appearance = _a.appearance, spacing = _a.spacing, mode = _a.mode, isSelected = _a.isSelected, shouldFitContainer = _a.shouldFitContainer, isOnlySingleIcon = _a.isOnlySingleIcon;
    var baseColors = getColors({
        appearance: appearance,
        key: isSelected ? 'selected' : 'default',
        mode: mode,
    });
    return __assign(__assign({ alignItems: 'baseline', borderWidth: 0, borderRadius: borderRadius, boxSizing: 'border-box', display: 'inline-flex', fontSize: 'inherit', fontStyle: 'normal', 
        // Chrome recently changed button so that they use 'arial' as the font family
        fontFamily: 'inherit', fontWeight: 500, maxWidth: '100%', 
        // Needed to position overlay
        position: 'relative', textAlign: 'center', textDecoration: 'none', transition: 'background 0.1s ease-out, box-shadow 0.15s cubic-bezier(0.47, 0.03, 0.49, 1.38)', whiteSpace: 'nowrap' }, baseColors), { cursor: 'pointer', height: heights[spacing], lineHeight: lineHeights[spacing], padding: isOnlySingleIcon ? singleIconPadding[spacing] : padding[spacing], verticalAlign: verticalAlign[spacing], width: shouldFitContainer ? '100%' : 'auto', 
        // justifyContent required for shouldFitContainer buttons with an icon inside
        justifyContent: 'center', 
        // Note: we cannot disable pointer events when there is an overlay.
        // That would be easy for styling, but it would start letting events through on disabled buttons
        // Disabling visited styles (just using the base colors)
        '&:visited': __assign({}, baseColors), '&:hover': __assign(__assign({}, getColors({
            appearance: appearance,
            key: isSelected ? 'selected' : 'hover',
            mode: mode,
        })), { textDecoration: !isSelected && (appearance === 'link' || appearance === 'subtle-link')
                ? 'underline'
                : 'inherit', 
            // background, box-shadow
            transitionDuration: '0s, 0.15s' }), '&:focus': __assign(__assign({}, getColors({
            appearance: appearance,
            key: isSelected ? 'focusSelected' : 'focus',
            mode: mode,
        })), { boxShadow: "0 0 0 2px " + colors.boxShadowColor[appearance].focus[mode], 
            // background, box-shadow
            transitionDuration: '0s, 0.2s', 
            // disabling browser focus outline
            outline: 'none' }), 
        // giving active styles preference by listing them after focus
        '&:active': __assign(__assign({}, getColors({
            appearance: appearance,
            key: isSelected ? 'selected' : 'active',
            mode: mode,
        })), { 
            // background, box-shadow
            transitionDuration: '0s, 0s' }), 
        // preventDefault prevents regular active styles from applying in Firefox
        '&[data-firefox-is-active="true"]': __assign(__assign({}, getColors({
            appearance: appearance,
            key: isSelected ? 'selected' : 'active',
            mode: mode,
        })), { 
            // background, box-shadow
            transitionDuration: '0s, 0s' }), 
        // Giving disabled styles preference over active by listing them after.
        // Not using '&:disabled' because :disabled is not a valid state for all element types
        // so we are targeting the attribute
        // Attributes have the same specificity a pesudo classes so we are overriding :disabled here
        '&[disabled]': __assign(__assign({}, getColors({ appearance: appearance, key: 'disabled', mode: mode })), { cursor: 'not-allowed', textDecoration: 'none' }), '&[data-has-overlay="true"]': {
            cursor: 'default',
            textDecoration: 'none',
        }, 
        // disabling hover and active color changes when there is an overlay, but the button is not disabled
        '&[data-has-overlay="true"]:not([disabled]):hover, &[data-has-overlay="true"]:not([disabled]):active': __assign({}, getColors({
            appearance: appearance,
            key: isSelected ? 'selected' : 'default',
            mode: mode,
        })), '&::-moz-focus-inner': {
            border: 0,
            margin: 0,
            padding: 0,
        } });
}
// inline-flex child
export function getIconStyle(_a) {
    var spacing = _a.spacing;
    return {
        alignSelf: 'center',
        display: 'flex',
        // icon size cannot grow and shrink
        flexGrow: 0,
        flexShrink: 0,
        lineHeight: 0,
        fontSize: 0,
        userSelect: 'none',
        margin: spacing === 'none' ? 0 : innerMargin.icon,
    };
}
// inline-flex child
export function getContentStyle(_a) {
    var spacing = _a.spacing;
    return {
        margin: spacing === 'none' ? 0 : innerMargin.content,
        // content can grow and shrink
        flexGrow: 1,
        flexShrink: 1,
        // ellipsis for overflow text
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    };
}
export function getFadingCss(_a) {
    var hasOverlay = _a.hasOverlay;
    return {
        transition: 'opacity 0.3s',
        opacity: hasOverlay ? 0 : 1,
    };
}
export var overlayCss = {
    // stretching to full width / height of button
    // this is important as we need it to still block
    // event if clicking in the button's own padding
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
    // Putting all children in the center
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
};
//# sourceMappingURL=css.js.map